import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FiBell, FiKey, FiLogOut, FiMenu, FiX } from "react-icons/fi";
import { useAuth } from "@/lib/auth";
import { getOverview, logout, OVERVIEW_REFRESH_MS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import LangSwitch from "@/components/LangSwitch";
import PasswordDialog from "@/components/PasswordDialog";
import { fmtNum, useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export default function Topbar({ menuOpen, onMenu }: { menuOpen: boolean; onMenu: () => void }) {
  const { user, setUser } = useAuth();
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState(false);
  // Same cache entry as the overview page: the beacon and the bell cost no extra poll there.
  // A port viewer has no NOC data at all (the API answers 403), so it gets neither.
  const noc = !!user && user.role !== "portviewer";
  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: getOverview,
    refetchInterval: OVERVIEW_REFRESH_MS,
    enabled: noc,
  });
  // The badge is what nobody has taken yet; the tooltip keeps the full count.
  const open = overview.data?.totals.alerts_unacked ?? overview.data?.totals.alerts_open ?? 0;
  const all = overview.data?.totals.alerts_open ?? 0;
  const crit = (overview.data?.totals.alerts_critical ?? 0) > 0;

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-rule bg-panel px-3 md:px-5">
      <Button
        variant="ghost"
        size="icon-sm"
        className="md:hidden"
        aria-label={menuOpen ? t("topbar.closeNav") : t("topbar.openNav")}
        aria-expanded={menuOpen}
        aria-controls="app-nav"
        onClick={onMenu}
      >
        {menuOpen ? <FiX /> : <FiMenu />}
      </Button>
      <Link to="/" className="flex items-center gap-2 text-[13px] font-semibold tracking-[0.22em] text-slate-100">
        <span className="grid size-5 place-items-center rounded-[3px] bg-signal/15 text-[10px] text-signal">J</span>
        <span className="uppercase">
          Jalabyte <span className="text-slate-500">Ops</span>
        </span>
      </Link>
      {noc && <Live updatedAt={overview.dataUpdatedAt} failed={overview.isError} />}
      <div className="flex-1" />

      {noc && (
        <Link
          to="/alerts"
          aria-label={t("topbar.attention", { n: open, all })}
          title={t("topbar.attention", { n: open, all })}
          className="relative grid size-8 place-items-center rounded-md text-slate-400 transition-colors hover:bg-white/[0.04] hover:text-slate-100"
        >
          <FiBell className="size-4" />
          {open > 0 && (
            <span
              className={cn(
                "data absolute -top-0.5 -right-0.5 min-w-4 rounded-full px-1 text-center text-[10px] leading-4 font-medium text-slate-950",
                crit ? "bg-signal-crit" : "bg-signal-warn",
              )}
            >
              {fmtNum(locale, open)}
            </span>
          )}
        </Link>
      )}
      <LangSwitch />
      {user && (
        <>
          <span
            className="hidden max-w-48 truncate text-xs text-slate-400 lg:inline"
            title={t("topbar.signedInAs", { email: user.email })}
          >
            {user.email}
          </span>
          <span className="eyebrow rounded border border-rule px-1.5 py-0.5 text-slate-300">{user.role}</span>
          {/* Available to every role, including a port viewer. */}
          <Button variant="ghost" size="icon-xs" onClick={() => setPw(true)} title={t("pw.changeTitle")} aria-label={t("pw.change")}>
            <FiKey />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            className="hidden md:inline-flex"
            disabled={busy}
            title={t("topbar.logout")}
            aria-label={t("topbar.logout")}
            onClick={async () => {
              setBusy(true);
              try {
                await logout();
                setUser(null);
              } finally {
                setBusy(false);
              }
            }}
          >
            <FiLogOut />
          </Button>
        </>
      )}
      {pw && <PasswordDialog onClose={() => setPw(false)} />}
    </header>
  );
}

/** "● LIVE · updated 4 s ago"; turns amber STALE after a failed poll or three missed ones. */
export function Live({
  updatedAt, failed, every = OVERVIEW_REFRESH_MS, className,
}: { updatedAt: number; failed: boolean; every?: number; className?: string }) {
  const { t } = useI18n();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!updatedAt) return null;
  const s = Math.max(0, Math.round((now - updatedAt) / 1000));
  const stale = failed || s * 1000 > every * 3;
  return (
    <span className={cn("hidden items-center gap-2 border-l border-rule pl-3 text-[11px] sm:inline-flex", className)} aria-live="off">
      <span className={cn("beacon size-1.5 rounded-full", stale ? "bg-signal-warn text-signal-warn" : "bg-signal-good text-signal-good")} />
      <span className={cn("eyebrow", stale ? "text-signal-warn" : "text-signal-good")}>
        {t(stale ? "live.stale" : "live.live")}
      </span>
      <span className="data text-slate-500">
        · {s < 60 ? t("live.agoS", { n: s }) : t("live.agoM", { n: Math.floor(s / 60) })}
      </span>
    </span>
  );
}
